import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Typography, CircularProgress, Stack, Button, Box, Paper, Card, CardContent, Chip } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import LockIcon from '@mui/icons-material/Lock';
import FeedbackButtons from 'components/FeedbackButtons';
import { motion } from 'framer-motion';
import { TrendingUp, Assessment, Business, Timeline, Lightbulb, ChevronRight } from '@mui/icons-material';

// Market Size Projection component with modern design
const MarketSizeProjection = ({ mockMarketProjection, formatMarketSize, handleTrialClick }) => {
  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 2,
        bgcolor: 'background.paper',
        position: 'relative',
        isolation: 'isolate',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          padding: '1px',
          borderRadius: 'inherit',
          background: 'linear-gradient(135deg, rgba(0,118,255,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          zIndex: -1
        },
        boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
        }
      }}
    >
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TrendingUp sx={{ color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Market Size Projection
          </Typography>
        </Box>

        <Box id="market-size-chart" sx={{ position: 'relative', width: '100%', height: 400, filter: 'blur(5px)', userSelect: 'none' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={mockMarketProjection} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis tickFormatter={(value) => formatMarketSize(value)} tick={{ style: { filter: 'blur(5px)' } }} />
              <Tooltip formatter={(value) => formatMarketSize(value)} />
              <Legend />
              <Bar dataKey="marketSize" fill="#8884d8" name="Market Size" />
            </BarChart>
          </ResponsiveContainer>
        </Box>

        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            opacity: 0,
            transition: 'opacity 0.3s',
            '&:hover': {
              opacity: 1
            },
            cursor: 'pointer',
            borderRadius: 2
          }}
          onClick={handleTrialClick}
        >
          <LockIcon />
          <Typography variant="caption" color="primary" ml={1}>
            Unlock
          </Typography>
        </Box>

        <Chip
          label="MOCK example, get PRO to get real data"
          size="small"
          color="warning"
          variant="outlined"
          sx={{ alignSelf: 'flex-start' }}
        />
      </Stack>
    </Box>
  );
};

const MarketOverview = ({ marketOverview, isProUser, handleTrialClick, analysisData }) => {
  const [hoveredItems, setHoveredItems] = useState({});
  const taskId = useSelector((state) => state.currentTaskReducer);

  const calculateMarketProjection = (initialSize, cagr) => {
    if (!initialSize || !cagr) return [];

    const currentYear = new Date().getFullYear();
    return Array.from({ length: 7 }, (_, index) => {
      const year = currentYear + index;
      const marketSize = initialSize * Math.pow(1 + cagr, index);
      return {
        year,
        marketSize: parseFloat(marketSize.toFixed(2))
      };
    });
  };

  const formatMarketSize = (value) => {
    if (value >= 1e9) {
      return `$${(value / 1e9).toFixed(2)}B`;
    } else if (value >= 1e6) {
      return `$${(value / 1e6).toFixed(2)}M`;
    } else {
      return `$${value.toFixed(2)}`;
    }
  };

  const marketProjection = marketOverview ? calculateMarketProjection(marketOverview.CurrentMarketSize, marketOverview.CAGR / 100) : [];

  const mockMarketProjection = [
    { year: new Date().getFullYear(), marketSize: 1000000 },
    { year: new Date().getFullYear() + 1, marketSize: 1100000 },
    { year: new Date().getFullYear() + 2, marketSize: 1210000 },
    { year: new Date().getFullYear() + 3, marketSize: 1331000 },
    { year: new Date().getFullYear() + 4, marketSize: 1464100 },
    { year: new Date().getFullYear() + 5, marketSize: 1610510 },
    { year: new Date().getFullYear() + 6, marketSize: 1771561 }
  ];

  const getIcon = (key) => {
    switch (key) {
      case 'Introduction':
        return <Lightbulb />;
      case 'MarketSize':
        return <Business />;
      case 'CAGR':
        return <TrendingUp />;
      default:
        return <Assessment />;
    }
  };

  const renderMarketSection = (key, value) => {
    const isIntroduction = key === 'Introduction';
    const isVisible = isIntroduction || isProUser;

    return (
      <motion.div whileHover={{ scale: 1.01 }} transition={{ duration: 0.2 }}>
        <Box
          sx={{
            p: 3,
            borderRadius: 2,
            bgcolor: 'background.paper',
            position: 'relative',
            isolation: 'isolate',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              padding: '1px',
              borderRadius: 'inherit',
              background: 'linear-gradient(135deg, rgba(0,118,255,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
              WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
              zIndex: -1
            },
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
            }
          }}
        >
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>{getIcon(key)}</Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {key}
              </Typography>
            </Box>

            {isVisible ? (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <ChevronRight sx={{ color: 'primary.main', fontSize: 20, mt: 0.5 }} />
                <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                  {key === 'MarketSize' ? formatMarketSize(value) : value}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                <Box
                  sx={{
                    filter: 'blur(7px)',
                    userSelect: 'none',
                    opacity: 0.75
                  }}
                >
                  <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                    {key === 'MarketSize' ? formatMarketSize(value) : value}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.6)',
                    opacity: 0,
                    transition: 'opacity 0.3s',
                    '&:hover': {
                      opacity: 1
                    },
                    cursor: 'pointer'
                  }}
                  onClick={handleTrialClick}
                >
                  <LockIcon />
                  <Typography variant="caption" color="primary" ml={1}>
                    Unlock
                  </Typography>
                </Box>
              </Box>
            )}
          </Stack>
        </Box>
      </motion.div>
    );
  };

  return (
    <Box id="market-overview" sx={{ mt: 5, mb: 8 }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <Card
          sx={{
            mb: 4,
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 3,
            boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(0,118,255,0.05) 0%, rgba(103,58,183,0.05) 50%, rgba(103,58,183,0) 100%)',
              zIndex: 0
            }}
          />

          <CardContent sx={{ position: 'relative', zIndex: 1 }}>
            <Stack spacing={3}>
              <Box sx={{ mb: 0 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    background: 'linear-gradient(135deg, #0070f3 0%, #6743b5 60%, rgba(103,58,183,0.7) 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    color: 'primary.main'
                  }}
                >
                  Market Overview
                </Typography>
                <Chip label="Powered by Askpot AI" size="small" color="primary" variant="outlined" />
              </Box>

              {!marketOverview && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {marketOverview !== null && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3
                  }}
                >
                  {Object.entries(marketOverview)
                    .sort(([keyA], [keyB]) => (keyA === 'Introduction' ? -1 : keyB === 'Introduction' ? 1 : 0))
                    .map(([key, value], index) => (
                      <React.Fragment key={index}>
                        {key !== 'CurrentMarketSize' && key !== 'CAGR' && (
                          <Box
                            onMouseEnter={() => setHoveredItems((prev) => ({ ...prev, [key]: true }))}
                            onMouseLeave={() => setHoveredItems((prev) => ({ ...prev, [key]: false }))}
                          >
                            {renderMarketSection(key, value)}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                              <FeedbackButtons
                                blockId={`market-overview-${key}`}
                                blockType="market-overview-section"
                                blockTitle={key}
                                blockContent={String(value)}
                                taskId={taskId}
                                serviceName={analysisData ? analysisData['Service Name'] : 'N/A'}
                                isVisible={!!hoveredItems[key]}
                              />
                            </Box>
                          </Box>
                        )}
                        {key === 'Introduction' && !isProUser && (
                          <Box
                            sx={{ gridColumn: '1 / -1' }}
                            onMouseEnter={() => setHoveredItems((prev) => ({ ...prev, 'market-projection': true }))}
                            onMouseLeave={() => setHoveredItems((prev) => ({ ...prev, 'market-projection': false }))}
                          >
                            <MarketSizeProjection
                              mockMarketProjection={mockMarketProjection}
                              formatMarketSize={formatMarketSize}
                              handleTrialClick={handleTrialClick}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                              <FeedbackButtons
                                blockId="market-projection"
                                blockType="market-projection"
                                blockTitle="Market Size Projection"
                                blockContent={JSON.stringify(mockMarketProjection)}
                                taskId={taskId}
                                serviceName={analysisData ? analysisData['Service Name'] : 'N/A'}
                                isVisible={!!hoveredItems['market-projection']}
                              />
                            </Box>
                          </Box>
                        )}
                      </React.Fragment>
                    ))}

                  {isProUser && marketProjection.length > 0 && (
                    <Box
                      sx={{ gridColumn: '1 / -1' }}
                      onMouseEnter={() => setHoveredItems((prev) => ({ ...prev, 'market-projection': true }))}
                      onMouseLeave={() => setHoveredItems((prev) => ({ ...prev, 'market-projection': false }))}
                    >
                      <Box
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: 'background.paper',
                          position: 'relative',
                          isolation: 'isolate',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            inset: 0,
                            padding: '1px',
                            borderRadius: 'inherit',
                            background:
                              'linear-gradient(135deg, rgba(0,118,255,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
                            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                            WebkitMaskComposite: 'xor',
                            maskComposite: 'exclude',
                            zIndex: -1
                          },
                          boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
                          }
                        }}
                      >
                        <Stack spacing={2}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Timeline sx={{ color: 'primary.main' }} />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              Market Size Projection
                            </Typography>
                          </Box>

                          <Typography variant="body2" align="center" color="text.secondary">
                            Current Market Size: {formatMarketSize(marketOverview.CurrentMarketSize)} | CAGR:{' '}
                            {marketOverview.CAGR ? `${marketOverview.CAGR.toFixed(2)}%` : 'N/A'}
                          </Typography>

                          <Box sx={{ position: 'relative', width: '100%', height: 400 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={marketProjection} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="year" />
                                <YAxis tickFormatter={(value) => formatMarketSize(value)} />
                                <Tooltip formatter={(value) => formatMarketSize(value)} />
                                <Legend />
                                <Bar dataKey="marketSize" fill="#8884d8" name="Market Size" />
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>

                          <Box sx={{ position: 'absolute', top: 100, right: 40, display: 'flex', alignItems: 'center' }}>
                            <Typography variant="caption" color="textSecondary" mr={1}>
                              Powered by
                            </Typography>
                            <img src="/favicon.svg" alt="Askpot Logo" style={{ height: 20 }} />
                            <Typography variant="h5" ml={1}>
                              Askpot
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                        <FeedbackButtons
                          blockId="market-projection"
                          blockType="market-projection"
                          blockTitle="Market Size Projection"
                          blockContent={JSON.stringify(marketProjection)}
                          taskId={taskId}
                          serviceName={analysisData ? analysisData['Service Name'] : 'N/A'}
                          isVisible={!!hoveredItems['market-projection']}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {!isProUser && (
                <Paper
                  elevation={0}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 3,
                    backdropFilter: 'blur(5px)',
                    backgroundColor: 'rgba(255, 255, 255, 0.6)',
                    textAlign: 'center',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h6" color="primary" align="center" mb={1}>
                    Start your FREE 7 days trial to get access to all Market Overview content!
                  </Typography>
                  <Button variant="contained" color="primary" sx={{ mt: 2 }} onClick={handleTrialClick}>
                    Unlock Market Overview
                  </Button>
                </Paper>
              )}
            </Stack>
          </CardContent>
        </Card>
      </motion.div>
    </Box>
  );
};

export default MarketOverview;
